"use client";

import React, { useRef } from "react";
import { cva } from "class-variance-authority";
import { motion, useScroll, useTransform } from "motion/react";
import { TextBlockContent, type TextBlockContentProps } from "./Text";
import { Image } from "@/components/ui/Image";
import { Video } from "@/components/ui/Video";
import type { IconName, MediaData, ThemeVariant } from "@/types";
import { GradientContainer } from "@/components/ui/GradientContainer";
import { Breakpoint, useCurrentBreakpoint } from "@/lib/hooks/useBreakpoint";
import { useWindowDimensions } from "@/lib/hooks";

const mediaTextVariants = cva("relative overflow-hidden", {
  variants: {
    variant: {
      light: "",
      dark: "",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

export interface FullSizeParallaxTeaserTextProps extends Omit<
  TextBlockContentProps,
  "arrowDirection"
> {
  variant?: ThemeVariant;
  backgroundMedia?: MediaData;
  foregroundMedia?: MediaData;
  animate?: boolean;
  isDarkened?: boolean;
}

export const FullSizeParallaxTeaser: React.FC<
  FullSizeParallaxTeaserTextProps
> = ({
  backgroundMedia,
  foregroundMedia,
  topline,
  headline,
  text,
  headingTag = "h2",
  headingStyle = "h2",
  variant = "light",
  button,
  animate = true,
  isDarkened = false,
  className = "",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { height: windowHeight } = useWindowDimensions();
  const breakpoint = useCurrentBreakpoint();
  const strokeWidthMap: Record<Breakpoint, number> = {
    "2xl": 4,
    xl: 3,
    lg: 3,
    md: 3,
    sm: 2,
    xs: 2,
  };
  const fontSize = Math.floor(windowHeight * 0.28);
  const strokeWidth = strokeWidthMap[breakpoint];

  const { scrollYProgress } = useScroll({
    target: animate ? containerRef : undefined,
    offset: ["start end", "end end"],
  });

  // container
  const width = useTransform(scrollYProgress, [0.4, 0.8], ["80%", "100%"]);
  const height = useTransform(scrollYProgress, [0.4, 0.8], ["80vh", "100vh"]);
  const borderRadius = useTransform(
    scrollYProgress,
    [0.6, 0.8],
    ["24px", "0px"]
  );
  const imageScale = useTransform(
    scrollYProgress,
    [0.4, 0.8, 1],
    [0.88, 1, 1.06]
  );

  const childX = useTransform(scrollYProgress, [0.4, 0.8], ["-10vw", "0vw"]);
  const childY = useTransform(scrollYProgress, [0.4, 0.8], ["-10vh", "0vh"]);

  const svgTextY = useTransform(scrollYProgress, [0.4, 1], ["40%", "30%"]);
  const svgTextX = useTransform(scrollYProgress, [0.4, 1], ["59%", "61%"]);

  const buttonVariant = button
    ? {
        label: button.label,
        link: button.link,
        icon: button.icon,
        color: "yellow" as const,
        variant: "secondary" as const,
      }
    : undefined;

  // Shared style for all direct children: full viewport size, pinned to
  // top-left of container, then offset by childX/childY to track viewport.
  const viewportChildStyle = animate
    ? {
        x: childX,
        y: childY,
        width: "100vw",
        height: "100vh",
      }
    : {};

  const renderBackgroundMedia = () => {
    if (!backgroundMedia) {
      return (
        <motion.div
          style={viewportChildStyle}
          className="absolute top-0 left-0"
        >
          <GradientContainer className="w-full h-full" />
        </motion.div>
      );
    }

    if (backgroundMedia.type === "image" && backgroundMedia.image) {
      return (
        <motion.div
          style={viewportChildStyle}
          className="absolute top-0 left-0"
        >
          {/* Background image — static, just fills the viewport child */}
          <motion.div
            style={animate ? { scale: imageScale } : {}}
            className="absolute inset-0"
          >
            <Image
              {...backgroundMedia.image}
              quality={80}
              fill
              objectFit="cover"
              sizes="100vw"
              className="size-full"
            />
          </motion.div>

          {/* SVG text — positioned within the viewport-sized child */}
          <svg
            className="absolute inset-0 size-full text-yellow select-none"
            aria-hidden
          >
            <motion.text
              x={svgTextX}
              y={svgTextY}
              fill="none"
              strokeWidth={strokeWidth}
              stroke="currentColor"
              dominantBaseline="middle"
              textAnchor="middle"
              fontSize={fontSize}
              fontWeight={900}
            >
              MUUUH!
            </motion.text>
          </svg>

          {/* Foreground image — has its own independent parallax */}
          {foregroundMedia?.type === "image" && foregroundMedia.image && (
            <Image
              {...foregroundMedia.image}
              quality={40}
              width={Math.floor(windowHeight * 0.6)}
              height={Math.floor(windowHeight * 0.8)}
              objectFit="contain"
              objectPosition="bottom"
              sizes="100vw"
              className="absolute -right-20 sm:-right-10 md:right-[8vw] lg:right-[20vw] bottom-0 will-change-transform object-bottom-right origin-bottom-right"
            />
          )}
        </motion.div>
      );
    }

    if (backgroundMedia.type === "video" && backgroundMedia.video) {
      return (
        <motion.div
          style={viewportChildStyle}
          className="absolute top-0 left-0"
        >
          <Video
            video={backgroundMedia.video}
            className="w-full h-full object-cover"
          />
        </motion.div>
      );
    }

    return null;
  };

  const content = (
    <motion.div
      style={animate ? { width, height, borderRadius } : {}}
      className={mediaTextVariants({
        variant,
        className: `mx-auto relative flex flex-col justify-center ${
          !animate ? "w-full min-h-[70svh]" : ""
        } ${className}`,
      })}
    >
      {renderBackgroundMedia()}

      {/* Overlay — also needs to track viewport */}
      {backgroundMedia && isDarkened && (
        <motion.div
          style={viewportChildStyle}
          className="absolute top-0 left-0 pointer-events-none bg-black/50"
        />
      )}

      <div className="container w-screen md:pt-48 pt-24">
        <div className="relative col-span-10 md:col-span-9 flex items-center">
          <TextBlockContent
            topline={topline}
            headline={headline}
            text={text}
            headingTag={headingTag}
            headingStyle={headingStyle}
            variant={variant}
            button={buttonVariant}
          />
        </div>
      </div>
    </motion.div>
  );

  if (!animate) return content;

  return (
    <div ref={containerRef} className="relative h-[150vh] w-full">
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">
        {content}
      </div>
    </div>
  );
};
